import { ReactElement, ReactNode } from 'react';

import styled, { CSSProperties } from 'styled-components';

import { Button, ButtonProps } from '@components/button';
import { IconProps } from '@components/icons';
import { Menu } from '@components/menu';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type SegmentMenuActions = ReactNode | (({ close }: { close(): void }) => ReactNode);

export type SegmentConfig = {
  id: string;
  icon?: ReactElement<IconProps>;
  text?: string;
  textColor?: Color;
  onClick?: () => void;
  menuActions?: SegmentMenuActions;
  // Width for this segment's menu; defaults to the Menu's own 16rem. Set it
  // when the menu holds content wider than that (e.g. the 280px inline
  // calendar in a date condition) — otherwise the menu clips and scrolls.
  menuWidth?: number | string;
  // See Menu's stickyPlacement.
  menuStickyPlacement?: boolean;
  style?: CSSProperties;
  textStyles?: CSSProperties;
  isGrowable?: boolean;
  shouldTruncate?: boolean;
  tooltip?: string;
};

export type MultiSegmentedFiltersProps = {
  segments: SegmentConfig[];
  dataTestid?: string;
  shouldWrap?: boolean;
};

const CHIP_RADIUS = pxToRem(8);
const CHIP_HEIGHT = pxToRem(32);
const SEAM = pxToRem(1);
const GROW_FLEX = '1 1 0';
const FIXED_FLEX = '0 0 auto';

// Pairs of 2, but a remainder of exactly 1 folds into the previous row so a
// trailing chip (typically a close ×) never sits alone on its own line.
export const chunkSegments = <T,>(segments: T[]): T[][] => {
  const count = segments.length;
  if (count === 0) return [];
  if (count <= 3) return [segments];
  const rows: T[][] = [];
  let index = 0;
  while (index < count) {
    const remaining = count - index;
    const chunkSize = remaining === 3 ? 3 : 2;
    rows.push(segments.slice(index, index + chunkSize));
    index += chunkSize;
  }
  return rows;
};

type CellPosition = { rowIdx: number; colIdx: number; rowCount: number; rowLength: number };

const cornerRadius = ({ rowIdx, colIdx, rowCount, rowLength }: CellPosition): string => {
  const topLeft = rowIdx === 0 && colIdx === 0 ? CHIP_RADIUS : 0;
  const topRight = rowIdx === 0 && colIdx === rowLength - 1 ? CHIP_RADIUS : 0;
  const bottomRight = rowIdx === rowCount - 1 && colIdx === rowLength - 1 ? CHIP_RADIUS : 0;
  const bottomLeft = rowIdx === rowCount - 1 && colIdx === 0 ? CHIP_RADIUS : 0;
  return `${topLeft} ${topRight} ${bottomRight} ${bottomLeft}`;
};

const renderMenuActions =
  (menuActions: SegmentMenuActions) =>
  ({ close }: { close(): void }): ReactNode =>
    typeof menuActions === 'function' ? menuActions({ close }) : menuActions;

export const MultiSegmentedFilters = ({
  segments,
  dataTestid,
  shouldWrap = false,
}: MultiSegmentedFiltersProps) => {
  const rows = shouldWrap ? chunkSegments(segments) : [segments];

  // Apply the dataTestid to the 'selected-value' segment when present so e2e
  // helpers can target the row by its value chip. Fall back to the first
  // segment with menuActions for rows whose segments don't include a
  // selected-value chip (e.g., simple two-segment rows).
  const selectedValueIndex = segments.findIndex((segment) => segment.id === 'selected-value');
  const segmentWithMenuIndex =
    selectedValueIndex !== -1
      ? selectedValueIndex
      : segments.findIndex((segment) => segment.menuActions);

  let flatIndex = -1;
  return (
    <Container $shouldWrap={shouldWrap}>
      {rows.map((rowSegments, rowIdx) => (
        <Row key={rowIdx}>
          {rowSegments.map((segment, colIdx) => {
            flatIndex += 1;
            return (
              <Segment
                key={segment.id}
                segment={segment}
                position={{ rowIdx, colIdx, rowCount: rows.length, rowLength: rowSegments.length }}
                shouldWrap={shouldWrap}
                dataTestid={flatIndex === segmentWithMenuIndex ? dataTestid : undefined}
              />
            );
          })}
        </Row>
      ))}
    </Container>
  );
};

type SegmentProps = {
  segment: SegmentConfig;
  position: CellPosition;
  shouldWrap: boolean;
  dataTestid?: string;
};

const Segment = ({ segment, position, shouldWrap, dataTestid }: SegmentProps) => {
  const isIconOnly = Boolean(segment.icon && !segment.text);
  const isInteractive = Boolean(segment.menuActions || segment.onClick);
  const flexValue = shouldWrap && segment.isGrowable ? GROW_FLEX : FIXED_FLEX;

  // Chip cell owns background + hover. Button's inline `background: transparent`
  // keeps Button's :hover from overriding ours.
  const buttonStyles: CSSProperties = {
    minHeight: CHIP_HEIGHT,
    maxHeight: CHIP_HEIGHT,
    padding: isIconOnly ? `0 ${pxToRem(4)}` : `0 ${pxToRem(12)}`,
    background: 'transparent',
    border: 'none',
    borderRadius: cornerRadius(position),
    cursor: isInteractive ? 'pointer' : 'default',
    minWidth: 0,
    maxWidth: '100%',
    width: '100%',
    justifyContent: isIconOnly ? 'center' : 'flex-start',
    ...segment.style,
  };

  const textStyles: CSSProperties = {
    ...(segment.shouldTruncate && {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'block',
      minWidth: 0,
    }),
    ...segment.textStyles,
  };

  const buttonProps: ButtonProps = {
    variant: 'secondary',
    text: segment.text,
    style: buttonStyles,
    color: segment.textColor,
    onClick: segment.onClick,
    textStyle: textStyles,
    ...(segment.icon && { iconLeft: segment.icon }),
    ...(dataTestid && { dataTestid }),
    ...(segment.tooltip && { tooltip: segment.tooltip }),
  };

  return (
    <ChipCell
      $cornerRadius={cornerRadius(position)}
      $flex={flexValue}
      $isInteractive={isInteractive}
      data-segment-id={segment.id}
    >
      {segment.menuActions ? (
        <Menu
          placement="bottom-start"
          trigger={Button}
          triggerProps={buttonProps}
          dataTestid={dataTestid}
          width={segment.menuWidth}
          stickyPlacement={segment.menuStickyPlacement}
        >
          {renderMenuActions(segment.menuActions)}
        </Menu>
      ) : (
        <Button {...buttonProps} />
      )}
    </ChipCell>
  );
};

const Container = styled.div<{ $shouldWrap: boolean }>((p) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  gap: SEAM,
  width: p.$shouldWrap ? '100%' : 'fit-content',
}));

const Row = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: SEAM,
  minWidth: 0,
  maxWidth: '100%',
  width: '100%',
  overflow: 'hidden',
});

const ChipCell = styled.div<{
  $cornerRadius: string;
  $flex: string;
  $isInteractive: boolean;
}>((p) => ({
  display: 'flex',
  flex: p.$flex,
  minWidth: 0,
  maxWidth: '100%',
  background: theme.color.uiTrans5,
  borderRadius: p.$cornerRadius,
  overflow: 'hidden',
  ...(p.$isInteractive && {
    '&:hover': {
      background: theme.color.uiTrans10,
    },
  }),
}));
